import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import { SiteStylesProvider } from "@/hooks/useSiteStyles";
import { ContactModalProvider } from "@/context/ContactModalContext";
import { BasketProvider } from "@/context/BasketContext";
import { ShopperAuthProvider } from "@/context/ShopperAuthContext";
import CheckoutPage from "@/pages/CheckoutPage";
import MyOrdersPage from "@/pages/MyOrdersPage";
import MyOrderDetailPage from "@/pages/MyOrderDetailPage";
import MyFavouritesPage from "@/pages/MyFavouritesPage";
import MyAlertsPage from "@/pages/MyAlertsPage";
import ComparePage from "@/pages/ComparePage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import { useAnalyticsTracker } from "@/hooks/useAnalyticsTracker";
import HomePage from "@/pages/HomePage";
import PropertyPage from "@/pages/PropertyPage";
import PropertyModal from "@/components/PropertyModal";
import ContactPage from "@/pages/ContactPage";
import PropertiesPage from "@/pages/PropertiesPage"; // legacy - kept for reference only
import JoinUsPage from "@/pages/JoinUsPage";
import TermsPage from "@/pages/TermsPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import PricesPage from "@/pages/PricesPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AboutPage from "@/pages/AboutPage";
import CookieConsent from "@/components/CookieConsent";
import BasketDrawer from "@/components/BasketDrawer";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminResetPasswordPage from "@/pages/AdminResetPasswordPage";
import AdminDashboard from "@/pages/AdminDashboard";
import PropertyAdminLoginPage from "@/pages/PropertyAdminLoginPage";
import ClaimPropertyPage from "@/pages/ClaimPropertyPage";
import LinkAccountPage from "@/pages/LinkAccountPage";
import PropertyAdminDashboard from "@/pages/PropertyAdminDashboard";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
// New Yopa-inspired pages
import ValuationPage from "@/pages/ValuationPage";
import OnlineValuationPage from "@/pages/OnlineValuationPage";
import TestimonialsPage from "@/pages/TestimonialsPage";
import GuidesPage from "@/pages/GuidesPage";
import BlogPage from "@/pages/BlogPage";
import BlogArticlePage from "@/pages/BlogArticlePage";
import MortgageCalculatorPage from "@/pages/MortgageCalculatorPage";
import MovingCostCalculatorPage from "@/pages/MovingCostCalculatorPage";
import OurAgentsPage from "@/pages/OurAgentsPage";
import RequestCallbackPage from "@/pages/RequestCallbackPage";
import ModularPage from "@/pages/ModularPage";
import CustomStaticPage from "@/pages/CustomStaticPage";
import CmsPageOverride from "@/components/CmsPageOverride";
import NavigationHistoryTracker from "@/components/NavigationHistoryTracker";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Analytics tracker — fires on every route change
function AnalyticsTracker() {
  useAnalyticsTracker();
  return null;
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // When the user hard-reloads at a URL like /property/<id> that was opened
  // as a modal earlier in the session, React Router preserves the previous
  // `state.background` in window.history.state. That would cause BOTH the
  // standalone page AND the modal to render. We detect a reload navigation
  // and scrub the stale background state on first mount.
  useEffect(() => {
    if (!location.state?.background) return;
    const navEntry = typeof performance !== "undefined" && performance.getEntriesByType
      ? performance.getEntriesByType("navigation")[0]
      : null;
    const isReload = navEntry?.type === "reload";
    if (isReload) {
      const { background, ...rest } = location.state || {};
      navigate(location.pathname + location.search, { replace: true, state: Object.keys(rest).length ? rest : null });
    }
  }, []);

  const background = location.state?.background;
  return (
    <>
      <ScrollToTop />
      <AnalyticsTracker />
      <NavigationHistoryTracker />
      <Routes location={background || location}>
        {/* Home page now uses ModularPage - will check for page composition with null slug */}
        <Route path="/" element={<ModularPage fixedSlug="home" />} />
        <Route path="/properties" element={<ModularPage fixedSlug="properties" />} />
        <Route path="/property/:id" element={<PropertyPage />} />
        <Route path="/contact" element={<CmsPageOverride slug="contact" fallback={ContactPage} />} />
        <Route path="/join-us" element={<CmsPageOverride slug="join-us" fallback={JoinUsPage} />} />
        <Route path="/terms" element={<CmsPageOverride slug="terms" fallback={TermsPage} />} />
        <Route path="/how-it-works" element={<CmsPageOverride slug="how-it-works" fallback={HowItWorksPage} />} />
        <Route path="/prices" element={<CmsPageOverride slug="prices" fallback={PricesPage} />} />
        <Route path="/privacy" element={<CmsPageOverride slug="privacy" fallback={PrivacyPage} />} />
        <Route path="/about" element={<CmsPageOverride slug="about" fallback={AboutPage} />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/property-admin/login" element={<PropertyAdminLoginPage />} />
        <Route path="/property-admin/claim" element={<ClaimPropertyPage />} />
        <Route path="/property-admin/link-account" element={<LinkAccountPage />} />
        <Route path="/property-admin/dashboard" element={<PropertyAdminDashboard />} />
        
        {/* New Yopa-inspired routes */}
        <Route path="/valuation" element={<CmsPageOverride slug="valuation" fallback={ValuationPage} />} />
        <Route path="/online-valuation" element={<CmsPageOverride slug="online-valuation" fallback={OnlineValuationPage} />} />
        <Route path="/testimonials" element={<CmsPageOverride slug="testimonials" fallback={TestimonialsPage} />} />
        <Route path="/guides" element={<CmsPageOverride slug="guides" fallback={GuidesPage} />} />
        <Route path="/blog" element={<CmsPageOverride slug="blog" fallback={BlogPage} />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />
        <Route path="/mortgage-calculator" element={<CmsPageOverride slug="mortgage-calculator" fallback={MortgageCalculatorPage} />} />
        <Route path="/moving-cost-calculator" element={<CmsPageOverride slug="moving-cost-calculator" fallback={MovingCostCalculatorPage} />} />
        <Route path="/our-agents" element={<CmsPageOverride slug="our-agents" fallback={OurAgentsPage} />} />
        <Route path="/request-callback" element={<CmsPageOverride slug="request-callback" fallback={RequestCallbackPage} />} />

        {/* Shop / checkout */}
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/my-orders" element={<MyOrdersPage />} />
        <Route path="/my-orders/:orderId" element={<MyOrderDetailPage />} />
        <Route path="/my-favourites" element={<MyFavouritesPage />} />
        <Route path="/my-alerts" element={<MyAlertsPage />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/cancel" element={<PaymentCancelPage />} />
        
        {/* Catch-all route - checks modular pages first, then custom pages */}
        <Route path="/:slug" element={<CustomStaticPage />} />
      </Routes>

      {/* Modal route — only renders when a background location is provided
          (i.e. the user clicked a property card from a listing). Direct URL
          access to /property/:id still resolves to the full standalone page
          via the routes above. */}
      {background && (
        <Routes>
          <Route path="/property/:id" element={<PropertyModal />} />
        </Routes>
      )}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <SiteStylesProvider>
        <div className="App">
          <BrowserRouter>
            <ContactModalProvider>
              <BasketProvider>
                <ShopperAuthProvider>
                  <AppRouter />
                  <CookieConsent />
                  <BasketDrawer />
                </ShopperAuthProvider>
              </BasketProvider>
            </ContactModalProvider>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </div>
      </SiteStylesProvider>
    </HelmetProvider>
  );
}

export default App;
